import { getType } from 'mime';
import {
    buildAuthHeaders,
    decryptArrayBuffer,
    resolveEncryption,
    toArrayBuffer,
} from './shared';
import { mainKeyProvider } from './main/mainKeyProvider';

const inferMimeType = ( url: string, response: Response ): string => {

    const disposition = response.headers.get( 'content-disposition' );
    const dispositionFile = disposition?.split( 'filename=' )[1]?.replace( /["';]+/g, '' );
    if ( dispositionFile ) {

        const fromDisposition = getType( dispositionFile );
        if ( fromDisposition ) {

            return fromDisposition;

        }

    }

    const contentType = response.headers.get( 'content-type' );
    if ( contentType && !contentType.includes( 'octet-stream' )) {

        return contentType;

    }

    const cleanUrl = url.split( '?' )[0].split( '#' )[0];
    const fromUrl = getType( cleanUrl );
    if ( fromUrl ) {

        return fromUrl;

    }

    return contentType ?? 'application/octet-stream';

};

export interface ILoadSecureMediaResult {
    blobUrl: string;
    mime: string;
}

export interface ILoadSecureMediaBlobResult {
    blob: Blob;
    mime: string;
    contentDisposition: string | null;
}

export interface ILoadSecureMediaOptions {
    signal?: AbortSignal;
}

/**
 * 主线程降级路径：
 * 1. 从共享 IDB 读出 URL 的加密元信息
 * 2. 按 mode 组装鉴权头（CMK / X-Access-Key / 无）
 * 3. fetch ArrayBuffer
 * 4. 若是 `native-encrypt` 则解密
 * 5. 包成明文 Blob 返回，供播放降级 / 下载复用
 */
export const loadSecureMediaAsBlob = async (
    url: string,
    options: ILoadSecureMediaOptions = {}
): Promise<ILoadSecureMediaBlobResult> => {

    const meta = await resolveEncryption( url );
    const headers = await buildAuthHeaders( meta, mainKeyProvider );

    // 仅 sseC / urlOnly 必须带鉴权头；plain 与 native（URL 自带签名）允许空头
    const requiresHeader = meta.mode === 'sseC' || meta.mode === 'urlOnly';
    if ( requiresHeader && Object.keys( headers ).length === 0 ) {

        throw new Error(
            `[secureMedia] meta resolved as ${meta.mode} but no auth headers built; `
            + 'IDB likely missing entry for this URL. Was the event list response synced before play?'
        );

    }

    // 与原 SW 拦截行为对齐：让浏览器按 <img>/<video crossorigin="anonymous"> 的
    // 默认 same-origin 策略决定是否带 cookie；显式 'omit' 会破坏部分 worker 的鉴权
    const response = await fetch( url, {
        headers,
        signal: options.signal
    });

    if ( !response.ok ) {

        throw new Error( `[secureMedia] fetch ${url} failed: ${response.status} ${response.statusText}` );

    }

    const buffer = new Uint8Array( await response.arrayBuffer());

    const decrypted = meta.mode === 'native' && meta.encryptInfo && meta.encryptDetail
        ? await decryptArrayBuffer( buffer, meta.encryptDetail, meta.encryptInfo.keySeed, mainKeyProvider )
        : buffer;

    const mime = inferMimeType( url, response );
    const blob = new Blob( [toArrayBuffer( decrypted )], { type: mime });

    return {
        blob,
        mime,
        contentDisposition: response.headers.get( 'content-disposition' )
    };

};

/**
 * 将主线程降级得到的明文 Blob 转成 Object URL。
 *
 * 调用方使用完毕后应调用 {@link releaseObjectUrl} 释放内存。
 */
export const loadSecureMediaAsObjectUrl = async (
    url: string,
    options: ILoadSecureMediaOptions = {}
): Promise<ILoadSecureMediaResult> => {

    const { blob, mime } = await loadSecureMediaAsBlob( url, options );
    const blobUrl = URL.createObjectURL( blob );

    return { blobUrl, mime };

};

/**
 * 安全释放 Object URL；空值或非 `blob:` URL 调用是空操作。
 */
export const releaseObjectUrl = ( blobUrl: string | null | undefined ): void => {

    if ( !blobUrl?.startsWith( 'blob:' )) {

        return;

    }
    try {

        URL.revokeObjectURL( blobUrl );

    } catch ( e ) {

        console.warn( '[secureMedia] revokeObjectURL failed', e );

    }

};
